/**
 * Yatay bir şeritte kaç öğenin sığdığını hesaplar; kalanlar "…" menüsüne iner.
 * Grid araç çubuğu ve üst menü aynı hesabı kullanıyor.
 *
 * Ölçüm iki aşamalı: öğeler ÖNCE tamamı görünürken ölçülüp genişlikleri saklanır, sonra
 * kesme uygulanır. Tek aşamalı ölçüm (gizle → yeniden ölç) salınıma giriyor: bir öğe
 * gizlenince yer açılıyor, geri geliyor, yine taşıyor.
 *
 * `itemCount` ya da kapsayıcı genişliği değişince yeniden hesaplanır.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

export interface UseOverflowCountOptions {
  /** Ölçülecek öğe sayısı. */
  itemCount: number;
  /** "…" düğmesi için ayrılan genişlik (px) — taşma varsa o da yer kaplar. */
  overflowButtonWidth?: number;
  /** Öğeler arası boşluk (px). */
  gap?: number;
  /** En az bu kadar öğe görünür kalsın. */
  minVisible?: number;
}

export function useOverflowCount(
  containerRef: RefObject<HTMLElement | null>,
  { itemCount, overflowButtonWidth = 40, gap = 4, minVisible = 0 }: UseOverflowCountOptions,
) {
  const [visibleCount, setVisibleCount] = useState(itemCount);
  /** Öğe genişlikleri, hepsi görünürken ölçülmüş hâlleriyle. */
  const widths = useRef<number[]>([]);
  const measured = useRef(false);

  // Öğe sayısı değişti: ölçümler geçersiz, hepsini göster ve yeniden ölç.
  useEffect(() => {
    measured.current = false;
    widths.current = [];
    setVisibleCount(itemCount);
  }, [itemCount]);

  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!measured.current) {
      const children = Array.from(el.children) as HTMLElement[];
      // Ölçüm yalnızca tüm öğeler ekrandayken geçerli; eksikse bir sonraki turda ölçülür.
      if (children.length < itemCount) return;
      widths.current = children.slice(0, itemCount).map((c) => c.offsetWidth);
      measured.current = true;
    }

    const available = el.clientWidth;
    if (available <= 0) return;

    let used = 0;
    let fits = 0;
    for (let i = 0; i < itemCount; i++) {
      const w = widths.current[i] ?? 0;
      const next = used + w + (i > 0 ? gap : 0);
      // Son öğe değilse "…" düğmesine de yer kalmalı.
      const reserve = i < itemCount - 1 ? overflowButtonWidth + gap : 0;
      if (next + reserve > available) break;
      used = next;
      fits++;
    }
    setVisibleCount(Math.max(minVisible, Math.min(itemCount, fits)));
  }, [containerRef, itemCount, gap, overflowButtonWidth, minVisible]);

  // Ölçüm boyama öncesinde yapılır: kullanıcı önce taşan, sonra kesilen bir çubuk görmesin.
  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => recompute());
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, recompute]);

  return visibleCount;
}
